import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { base44 } from "@voult/api/base44Client";
import { ShieldCheck, Loader2 } from "lucide-react";
import {
  tokenizePan, maskPan, last4Of, luhnValid,
  detectBrand, formatCardNumber, formatExpiry,
} from "@voult/lib/tokenizer";

const THEMES = [
  { id: "midnight", cls: "bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900" },
  { id: "emerald", cls: "bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900" },
  { id: "sunset", cls: "bg-gradient-to-br from-rose-900 via-orange-900 to-slate-900" },
  { id: "royal", cls: "bg-gradient-to-br from-violet-900 via-purple-950 to-slate-900" },
  { id: "graphite", cls: "bg-gradient-to-br from-zinc-800 via-zinc-900 to-black" },
];

export default function AddCard() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ cardholder_name: "", pan: "", expiry: "", cvc: "", nickname: "" });
  const [theme, setTheme] = useState("midnight");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const valid = luhnValid(form.pan);
  const brand = detectBrand(form.pan);
  const expMatch = /^(0[1-9]|1[0-2])\/(\d{2})$/.exec(form.expiry);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.cardholder_name.trim()) return setError("Cardholder name is required.");
    if (!valid) return setError("Please enter a valid card number.");
    if (!expMatch) return setError("Expiry must be MM/YY.");
    if (form.cvc.length < 3) return setError("CVC must be at least 3 digits.");
    setSubmitting(true);
    try {
    await base44.entities.Card.create({
      cardholder_name: form.cardholder_name.trim(),
      masked_number: maskPan(form.pan),
      last4: last4Of(form.pan),
      token: tokenizePan(form.pan),
      pan: form.pan,
      brand,
      expiry_month: expMatch[1],
      expiry_year: expMatch[2],
      nickname: form.nickname.trim(),
      color_theme: theme,
    });
    await base44.entities.ActivityLog.create({ action: "card_added", entity_type: "Card", details: `Card ending in ${last4Of(form.pan)} added` });
    navigate("/voult/vault-list");
    } catch (err) {
      setError(err.response?.data?.detail?.toString() || "Could not save card.");
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-lg mx-auto">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Add Card</h1>
      <p className="text-sm text-slate-500 mb-6">Your card is tokenized instantly. The full number is encrypted by CITIDAN and never shown again.</p>

      <form onSubmit={submit} className="space-y-5 bg-white rounded-2xl border border-slate-200 p-6">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Cardholder name</label>
          <input data-testid="voult-addcard-name" value={form.cardholder_name} onChange={(e) => setForm((f) => ({ ...f, cardholder_name: e.target.value }))}
            placeholder="John Doe"
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5" />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Card number</label>
          <div className="relative">
            <input data-testid="voult-addcard-pan" value={form.pan} onChange={(e) => setForm((f) => ({ ...f, pan: formatCardNumber(e.target.value) }))}
              placeholder="4242 4242 4242 4242" inputMode="numeric"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 pr-16 font-mono text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{form.pan ? brand : ""}</span>
          </div>
          {form.pan && !valid && <p className="mt-1 text-xs text-rose-500">Invalid card number.</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Expiry</label>
            <input data-testid="voult-addcard-expiry" value={form.expiry} onChange={(e) => setForm((f) => ({ ...f, expiry: formatExpiry(e.target.value) }))}
              placeholder="MM/YY" inputMode="numeric"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 font-mono text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">CVC</label>
            <input data-testid="voult-addcard-cvc" value={form.cvc} onChange={(e) => setForm((f) => ({ ...f, cvc: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
              placeholder="123" type="password" inputMode="numeric"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 font-mono text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Nickname (optional)</label>
          <input data-testid="voult-addcard-nickname" value={form.nickname} onChange={(e) => setForm((f) => ({ ...f, nickname: e.target.value }))}
            placeholder="Daily spend"
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5" />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-2">Card theme</label>
          <div className="flex gap-2">
            {THEMES.map((t) => (
              <button type="button" key={t.id} onClick={() => setTheme(t.id)}
                className={`h-9 w-12 rounded-lg ${t.cls} ring-2 transition ${theme === t.id ? "ring-slate-900 ring-offset-2" : "ring-transparent"}`} />
            ))}
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-xl bg-emerald-50 p-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
          <p className="text-xs text-slate-600">Your PAN is <strong>tokenized instantly</strong> and stored only encrypted. CVC is never stored.</p>
        </div>

        {error && <p data-testid="voult-addcard-error" className="text-sm text-rose-500">{error}</p>}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={() => navigate("/voult/vault-list")}
            className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition">Cancel</button>
          <button data-testid="voult-addcard-submit" type="submit" disabled={submitting}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition disabled:opacity-60">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Tokenizing…</> : "Tokenize & save"}
          </button>
        </div>
      </form>
    </div>
  );
}